import { useState, useCallback, useRef } from 'react';

const CLE_HIST = '3f_hist';

function genSessionId() {
  let s = sessionStorage.getItem('3f_sid');
  if (!s) {
    s = 's_' + Date.now() + '_' + Math.random().toString(36).slice(2, 7);
    sessionStorage.setItem('3f_sid', s);
  }
  return s;
}

function chargerHistorique() {
  try {
    const h = localStorage.getItem(CLE_HIST);
    if (h) return JSON.parse(h);
  } catch (e) {}
  return [];
}

export function useChat(config) {
  const [messages, setMessages] = useState(chargerHistorique);
  const [enCours, setEnCours] = useState(false);
  const [fichiers, setFichiers] = useState([]);
  const compteurTyping = useRef(0);

  // ── Sauvegarder historique ──
  const sauvegarder = useCallback((msgs) => {
    try { localStorage.setItem(CLE_HIST, JSON.stringify(msgs)); } catch (e) {}
  }, []);

  // ── Ajouter un message ──
  const ajouterMessage = useCallback((role, texte, pjs = [], data = null) => {
    const msg = {
      id: Date.now() + '_' + Math.random().toString(36).slice(2, 5),
      role,
      texte,
      pjs,
      data,
      heure: new Date().toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }),
      date: new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' }),
    };
    setMessages(prev => {
      const updated = [...prev, msg];
      sauvegarder(updated);
      return updated;
    });
    return msg;
  }, [sauvegarder]);

  // ── Ajouter fichiers ──
  const ajouterFichiers = useCallback((liste) => {
    Array.from(liste).forEach(file => {
      if (fichiers.length >= 10) return;
      const reader = new FileReader();
      reader.onload = (e) => {
        setFichiers(prev => [...prev, {
          id: Date.now() + '_' + file.name,
          file,
          nom: file.name,
          type: file.type,
          sz: formatSz(file.size),
          b64: e.target.result.split(',')[1],
        }]);
      };
      reader.readAsDataURL(file);
    });
  }, [fichiers.length]);

  const supprimerFichier = useCallback((id) => {
    setFichiers(prev => prev.filter(f => f.id !== id));
  }, []);

  // ── Vider conversation ──
  const vider = useCallback(() => {
    setMessages([]);
    try { localStorage.removeItem(CLE_HIST); } catch (e) {}
    sessionStorage.removeItem('3f_sid');
  }, []);

  // ── Envoyer message ──
  const envoyer = useCallback(async (texte) => {
    if ((!texte.trim() && !fichiers.length) || enCours) return;

    const pjsEnvoi = [...fichiers];
    setFichiers([]);

    // Message utilisateur
    ajouterMessage('user', texte.trim(), pjsEnvoi);

    // Message typing
    const typingId = 'typing_' + (++compteurTyping.current);
    setMessages(prev => [...prev, { id: typingId, role: 'typing' }]);
    setEnCours(true);

    const supprimerTyping = () => {
      setMessages(prev => prev.filter(m => m.id !== typingId));
    };

    // Pas de webhook
    if (!config.url) {
      await pause(600);
      supprimerTyping();
      ajouterMessage('bot', '⚠️ **Webhook non configuré**\n\nAppuyez sur ⚙️ en haut à droite pour renseigner l\'URL de votre workflow n8n.');
      setEnCours(false);
      return;
    }

    try {
      let res;
      if (config.format === 'formdata' && pjsEnvoi.length) {
        const fd = new FormData();
        fd.append('message', texte);
        fd.append('system_prompt', config.prompt);
        fd.append('session_id', genSessionId());
        fd.append('timestamp', new Date().toISOString());
        pjsEnvoi.forEach((f, i) => fd.append('file_' + i, f.file, f.nom));
        res = await fetch(config.url, { method: 'POST', body: fd });
      } else {
        res = await fetch(config.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            message: texte,
            system_prompt: config.prompt,
            session_id: genSessionId(),
            timestamp: new Date().toISOString(),
            fichiers: pjsEnvoi.map(f => ({ nom: f.nom, type: f.type, taille: f.sz, contenu_base64: f.b64 })),
          }),
        });
      }

      supprimerTyping();

      if (res.ok) {
        // ── FIX : lire le body UNE SEULE FOIS ──
        const raw = await res.text();
        let data;
        try {
          data = JSON.parse(raw);
        } catch {
          data = { output: raw };
        }

        const reponse =
          data?.output || data?.response || data?.message || data?.text || data?.reply ||
          (Array.isArray(data) && (data[0]?.output || data[0]?.text)) ||
          raw;

        ajouterMessage('bot', reponse, [], data);
      } else {
        // ── FIX : lire le body UNE SEULE FOIS pour l'erreur ──
        const errBody = await res.text();
        throw new Error('HTTP ' + res.status + (errBody ? ' — ' + errBody.slice(0, 100) : ''));
      }
    } catch (e) {
      supprimerTyping();
      ajouterMessage('bot', `❌ **Erreur** : \`${e.message}\`\n\nVérifiez que votre workflow n8n est actif.`);
    }

    setEnCours(false);
  }, [fichiers, enCours, config, ajouterMessage]);

  return { messages, enCours, fichiers, ajouterFichiers, supprimerFichier, envoyer, vider };
}

function formatSz(b) {
  if (b < 1024) return b + 'o';
  if (b < 1048576) return (b / 1024).toFixed(1) + 'Ko';
  return (b / 1048576).toFixed(1) + 'Mo';
}

function pause(ms) { return new Promise(r => setTimeout(r, ms)); }
